/**
 * Dashboard Hooks
 *
 * TanStack Query hooks for dashboard data including:
 * - KPIs with week-over-week changes
 * - Time-series trend data
 * - Recent assessments
 * - Operators needing attention
 * - Department breakdown
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// Query keys
export const dashboardKeys = {
  all: ['dashboard'] as const,
  kpis: () => [...dashboardKeys.all, 'kpis'] as const,
  trends: (days?: number) => [...dashboardKeys.all, 'trends', days] as const,
  recent: (limit?: number) => [...dashboardKeys.all, 'recent', limit] as const,
  attention: () => [...dashboardKeys.all, 'attention'] as const,
  departments: () => [...dashboardKeys.all, 'departments'] as const,
  operatorFeedback: () => [...dashboardKeys.all, 'operator-feedback'] as const,
};

// Response types
interface KpiData {
  operators: {
    value: number;
    label: string;
  };
  sops: {
    value: number;
    label: string;
  };
  assessments: {
    value: number;
    thisWeek: number;
    /** Assessments in the last 30 days (G-20). An older API omits it: read with `?? 0`. */
    last30Days: number;
    change: number;
    label: string;
  };
  averageScore: {
    value: number | null;
    thisWeek: number | null;
    change: number | null;
    label: string;
  };
}

interface TrendDataPoint {
  date: string;
  count: number;
  avgScore: number | null;
  avgCompliance: number | null;
  avgEfficiency: number | null;
  avgSafety: number | null;
}

interface RecentAssessment {
  id: string;
  assessmentType: 'auto' | 'manual';
  overallScore: number;
  assessedAt: string;
  operator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  sop: {
    id: string;
    title: string;
  };
}

interface AttentionOperator {
  id: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  departmentName: string | null;
  reason: 'low_score' | 'stale_assessments' | 'no_assessments';
  avgScore: number | null;
  assessmentCount: number;
  lastAssessment: string | null;
}

interface DepartmentStats {
  id: string;
  name: string;
  operatorCount: number;
  assessmentCount: number;
  avgScore: number | null;
}

// ============================================
// QUERIES
// ============================================

/**
 * Fetch dashboard KPIs
 */
export function useDashboardKpis() {
  const api = useApi();

  return useQuery({
    queryKey: dashboardKeys.kpis(),
    queryFn: () => api.get<KpiData>('/api/dashboard/kpis'),
    refetchInterval: 60000, // Refresh every minute
  });
}

/**
 * Fetch trend data for charts
 */
export function useDashboardTrends(days = 30) {
  const api = useApi();

  return useQuery({
    queryKey: dashboardKeys.trends(days),
    queryFn: () => api.get<TrendDataPoint[]>(`/api/dashboard/trends?days=${days}`),
    refetchInterval: 5 * 60000, // Refresh every 5 minutes
  });
}

/**
 * Fetch recent assessments
 */
export function useDashboardRecent(limit = 10) {
  const api = useApi();

  return useQuery({
    queryKey: dashboardKeys.recent(limit),
    queryFn: () => api.get<RecentAssessment[]>(`/api/dashboard/recent?limit=${limit}`),
    refetchInterval: 60000, // Refresh every minute
  });
}

/**
 * Fetch operators needing attention
 */
export function useDashboardAttention() {
  const api = useApi();

  return useQuery({
    queryKey: dashboardKeys.attention(),
    queryFn: () => api.get<AttentionOperator[]>('/api/dashboard/attention'),
    refetchInterval: 5 * 60000, // Refresh every 5 minutes
  });
}

/**
 * Fetch department breakdown
 */
export function useDashboardDepartments() {
  const api = useApi();

  return useQuery({
    queryKey: dashboardKeys.departments(),
    queryFn: () => api.get<DepartmentStats[]>('/api/dashboard/departments'),
    refetchInterval: 5 * 60000, // Refresh every 5 minutes
  });
}

// ============================================
// OPERATOR FEEDBACK
// ============================================

interface FeedbackRateBucket {
  positive: number;
  total: number;
  rate: number | null;
}

interface RecentFeedbackItem {
  id: string;
  feedbackType: 'assessment_fairness' | 'training_rating';
  sentiment: 'positive' | 'negative';
  comment: string | null;
  isAnonymous: boolean;
  createdAt: string;
  operatorName: string | null;
}

export interface OperatorFeedbackSummary {
  assessmentFairness: FeedbackRateBucket;
  trainingRating: FeedbackRateBucket;
  recent: RecentFeedbackItem[];
}

/**
 * Fetch operator feedback summary for the dashboard card
 */
export function useOperatorFeedbackSummary(days = 30) {
  const api = useApi();

  return useQuery({
    queryKey: dashboardKeys.operatorFeedback(),
    queryFn: () =>
      api.get<OperatorFeedbackSummary>(
        `/api/dashboard/operator-feedback/summary?days=${days}`
      ),
    refetchInterval: 5 * 60000, // Refresh every 5 minutes
  });
}
